import { useState } from 'react';
import { Link } from 'react-router-dom';
import { documentHref } from '@/lib/chat-sources';
import { listWritableFolders, saveAnswerToFolder } from '@/lib/chat-files';
import type { ChatCitation } from '@shared/types/chat-sources';

/**
 * D4 — « Ranger dans… » under an answer of the chat: choose a folder, keep or
 * change the proposed title, and the answer (with what it cites) becomes a
 * document of that folder. Nothing is written before the click.
 */

const citationLine = (citation: ChatCitation): string => {
  if (citation.kind === 'document') return `[${citation.n}] ${citation.title} — « ${citation.excerpt} »`;
  if (citation.kind === 'web') return `[${citation.n}] ${citation.title} — ${citation.url}`;
  if (citation.kind === 'record') return `[${citation.n}] ${citation.title} (${citation.app})`;
  if (citation.kind === 'record_set') return `[${citation.n}] ${citation.title} (${citation.app})`;
  return `[${citation.n}] ${citation.title}`;
};

export const SaveAnswerMenu = ({ answer, question, citations }: { answer: string; question: string; citations: ChatCitation[] }) => {
  const [folders, setFolders] = useState<Array<{ id: string; name: string }> | null>(null);
  const [folderId, setFolderId] = useState<string | null>(null);
  const [title, setTitle] = useState(question.trim().slice(0, 120));
  const [saved, setSaved] = useState<{ documentId: string; folder: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (saved) {
    return (
      <p className="mt-2 text-sm text-domino-inkSoft">
        Rangé dans {saved.folder} · <Link to={documentHref(saved.documentId)} className="text-domino-aubergine hover:underline">Ouvrir</Link>
      </p>
    );
  }

  const open = async () => {
    setError(null);
    try {
      setFolders(await listWritableFolders());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Dossiers indisponibles.');
    }
  };

  const save = async () => {
    const folder = folders?.find((item) => item.id === folderId);
    if (!folder || !title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const content = [answer, ...(citations.length ? ['', 'Sources', ...citations.map(citationLine)] : [])].join('\n');
      const documentId = await saveAnswerToFolder({ folderId: folder.id, title: title.trim(), content });
      setSaved({ documentId, folder: folder.name });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'La réponse n’a pas été rangée.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 text-sm">
      {!folders ? (
        <button type="button" onClick={() => void open()} className="text-domino-inkSoft hover:text-domino-aubergine">Ranger dans…</button>
      ) : folders.length === 0 ? (
        <p className="text-domino-inkSoft">
          Aucun dossier où ranger : <Link to="/profil?tab=documents" className="text-domino-aubergine hover:underline">Profil › Documents</Link>
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          <select aria-label="Dossier" value={folderId ?? ''} onChange={(event) => setFolderId(event.target.value || null)}
            className="w-full rounded-lg border border-domino-cardBorder bg-domino-warm px-2 py-1">
            <option value="">Dossier</option>
            {folders.map((folder) => <option key={folder.id} value={folder.id}>{folder.name}</option>)}
          </select>
          <input aria-label="Titre" value={title} onChange={(event) => setTitle(event.target.value)}
            className="w-full rounded-lg border border-domino-cardBorder bg-domino-warm px-2 py-1" />
          <div className="flex items-center gap-3">
            <button type="button" disabled={!folderId || !title.trim() || saving} onClick={() => void save()}
              className="rounded-lg bg-domino-aubergine px-3 py-1 text-white disabled:opacity-40">Ranger</button>
            <button type="button" onClick={() => setFolders(null)} className="text-domino-inkSoft hover:text-domino-ink">Annuler</button>
          </div>
        </div>
      )}
      {error && <p className="mt-1 text-domino-inkSoft">{error}</p>}
    </div>
  );
};
